import { Audio } from '../../shared/audio';
import { STAR_THRESHOLDS } from '../../shared/config';
import {
  addHighScore,
  getHighScores,
  getTopScore,
  isNewHighScore,
  type HighScoreEntry,
} from '../../shared/highScores';
import { createConfetti } from '../ui/confetti';
import { createDotsBg } from '../ui/decor';
import { createHighScoreCapture, type HighScoreCapture } from '../ui/highScoreCapture';
import { createHighScorePodium, type HighScorePodium } from '../ui/highScorePodium';
import { createPlayIcon, createStarIcon } from '../ui/icons';
import { createMascot, type Mascot } from '../ui/mascot';
import type { Screen, ScreenContext, GameOverResult } from './types';

interface MessageOption {
  readonly min: number;
  readonly text: string;
  readonly color: string;
}

const MESSAGES: ReadonlyArray<MessageOption> = [
  { min: 200, text: 'AMAZING!!', color: 'var(--teal-deep)' },
  { min: 100, text: 'Great job!', color: 'var(--purple)' },
  { min: 30, text: 'Nice try!', color: 'var(--teal)' },
  { min: 0, text: 'Good effort!', color: 'var(--orange)' },
];

function pickMessage(score: number): MessageOption {
  return MESSAGES.find((m) => score >= m.min) ?? MESSAGES[MESSAGES.length - 1]!;
}

function starsForScore(score: number): number {
  let stars = 0;
  for (const threshold of STAR_THRESHOLDS) {
    if (score >= threshold) stars += 1;
  }
  return stars;
}

function buildStat(testid: string, label: string, value: string, color: string): HTMLDivElement {
  const wrap = document.createElement('div');
  wrap.className = 'gameover-stat';
  const lab = document.createElement('div');
  lab.className = 'gameover-stat-label';
  lab.textContent = label;
  const val = document.createElement('div');
  val.className = 'gameover-stat-value headline';
  val.dataset.testid = testid;
  val.textContent = value;
  val.style.color = color;
  wrap.append(lab, val);
  return wrap;
}

const CHEER_THRESHOLD = 30;

export function createGameOverScreen(): Screen {
  let container: HTMLDivElement | null = null;
  let mascot: Mascot | null = null;
  let capture: HighScoreCapture | null = null;
  let podium: HighScorePodium | null = null;
  let podiumSlot: HTMLDivElement | null = null;

  function renderPodium(scores: ReadonlyArray<HighScoreEntry>, highlightDate: number | null): void {
    if (!podiumSlot) return;
    podium?.destroy();
    podium = createHighScorePodium({ scores, highlightDate });
    if (podium) podiumSlot.appendChild(podium.element);
  }

  function mount(root: HTMLElement, ctx: ScreenContext): void {
    const result: GameOverResult = ctx.params?.result ?? {
      score: 0,
      hits: 0,
      misses: 0,
      bestCombo: 0,
    };

    if (result.score >= CHEER_THRESHOLD) Audio.cheer();
    else Audio.sad();

    container = document.createElement('div');
    container.className = 'gameover-screen';
    container.dataset.testid = 'gameover-screen';
    container.appendChild(createDotsBg());

    if (result.score >= CHEER_THRESHOLD) {
      container.appendChild(createConfetti().element);
    }

    const panel = document.createElement('div');
    panel.className = 'gameover-panel';

    const mascotWrap = document.createElement('div');
    mascotWrap.className = 'gameover-mascot-pop';
    mascot = createMascot(7.5);
    mascot.setMood(result.score >= CHEER_THRESHOLD ? 'cheer' : 'sad');
    mascotWrap.appendChild(mascot.element);
    panel.appendChild(mascotWrap);

    const body = document.createElement('div');
    body.className = 'gameover-body';

    const message = pickMessage(result.score);
    const headline = document.createElement('h1');
    headline.className = 'gameover-headline headline';
    headline.style.color = message.color;
    headline.textContent = message.text;
    body.appendChild(headline);

    const previousBest = getTopScore();
    if (result.score > previousBest && previousBest > 0) {
      const beatBest = document.createElement('div');
      beatBest.className = 'gameover-beat-best headline wiggle';
      beatBest.dataset.testid = 'beat-your-best';
      beatBest.textContent = '★ BEAT YOUR BEST ★';
      body.appendChild(beatBest);
    }

    const stars = starsForScore(result.score);
    const starsRow = document.createElement('div');
    starsRow.className = 'gameover-stars';
    for (let i = 0; i < 3; i += 1) {
      const slot = document.createElement('div');
      slot.className = 'gameover-star';
      if (i < stars) {
        slot.dataset.on = 'true';
        slot.style.setProperty('--delay', `${(i * 0.2 + 0.3).toFixed(2)}s`);
      }
      slot.appendChild(createStarIcon(5));
      starsRow.appendChild(slot);
    }
    body.appendChild(starsRow);

    const stats = document.createElement('div');
    stats.className = 'gameover-stats';
    stats.appendChild(buildStat('stat-score', 'Score', String(result.score), 'var(--teal-deep)'));
    stats.appendChild(buildStat('stat-hits', 'Hits', String(result.hits), 'var(--teal)'));
    stats.appendChild(
      buildStat('stat-bestCombo', 'Best Combo', `×${result.bestCombo}`, 'var(--purple)'),
    );
    body.appendChild(stats);

    const actions = document.createElement('div');
    actions.className = 'gameover-actions';

    const playAgain = document.createElement('button');
    playAgain.type = 'button';
    playAgain.className = 'chunky-btn teal';
    playAgain.dataset.testid = 'play-again-btn';
    playAgain.appendChild(createPlayIcon(2));
    const playLabel = document.createElement('span');
    playLabel.textContent = 'PLAY AGAIN';
    playAgain.appendChild(playLabel);
    playAgain.addEventListener('click', () => ctx.goto('game'));

    const menu = document.createElement('button');
    menu.type = 'button';
    menu.className = 'chunky-btn yellow small';
    menu.dataset.testid = 'menu-btn';
    menu.textContent = '⌂ Menu';
    menu.addEventListener('click', () => ctx.goto('menu'));

    actions.append(playAgain, menu);
    body.appendChild(actions);

    panel.appendChild(body);
    container.appendChild(panel);

    podiumSlot = document.createElement('div');
    podiumSlot.className = 'gameover-podium-wrap';
    container.appendChild(podiumSlot);

    renderPodium(getHighScores(), null);

    root.appendChild(container);

    if (isNewHighScore(result.score)) {
      capture = createHighScoreCapture({
        score: result.score,
        hits: result.hits,
        bestCombo: result.bestCombo,
        onSave: (input) => {
          const saved = addHighScore(input);
          renderPodium(getHighScores(), saved.date);
          capture?.destroy();
          capture = null;
        },
        onSkip: () => {
          capture?.destroy();
          capture = null;
        },
      });
      container.appendChild(capture.element);
    }
  }

  function unmount(): void {
    capture?.destroy();
    capture = null;
    podium?.destroy();
    podium = null;
    podiumSlot = null;
    mascot?.destroy();
    mascot = null;
    container?.remove();
    container = null;
  }

  return { mount, unmount };
}
